import { useState, useEffect, useCallback } from 'react';
import { 
  User, 
  Plus, 
  Trash2, 
  Edit2,
  Save,
  X,
  Loader2,
  Building2,
  Stethoscope,
  MapPin,
  Sparkles,
  Globe2,
  Users,
  Activity
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from '@/components/ui/dialog';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from '@/components/ui/alert-dialog';
import { Skeleton } from '@/components/ui/skeleton';
import { toast } from 'sonner';
import { getPIs, createPI, updatePI, deletePI, extractPIFromCV, refreshPIDemographics } from '@/lib/api';

const EMPTY_FORM = {
  name: '',
  title: '',
  specialty: '',
  therapeutic_area: '',
  site_location: '',
  subspecialty: '',
  practice_type: '',
  annual_patient_volume_min: '',
  annual_patient_volume_max: '',
  locations: '',
  geographic_location: '',
  years_in_practice: '',
  competing_studies: '',
  board_certifications: '',  // comma-separated in UI
  research_experience: '',   // newline-separated in UI
};

export default function Settings() {
  const [pis, setPIs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingPI, setEditingPI] = useState(null);
  
  const [formData, setFormData] = useState(EMPTY_FORM);
  // Per-PI button busy states for "Re-extract from CV" / "Refresh demographics"
  const [busyPi, setBusyPi] = useState({});

  const loadPIs = useCallback(async () => {
    try {
      const data = await getPIs();
      setPIs(data);
    } catch (error) {
      toast.error('Failed to load PIs');
    } finally {
      setLoading(false);
    }
    // Module-level imports + stable setters only.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    loadPIs();
  }, [loadPIs]);

  const resetForm = () => {
    setFormData(EMPTY_FORM);
    setEditingPI(null);
  };

  const handleOpenDialog = (pi = null) => {
    if (pi) {
      setEditingPI(pi);
      setFormData({
        name: pi.name || '',
        title: pi.title || '',
        specialty: pi.specialty || '',
        therapeutic_area: pi.therapeutic_area || '',
        site_location: pi.site_location || '',
        subspecialty: pi.subspecialty || '',
        practice_type: pi.practice_type || '',
        annual_patient_volume_min: pi.annual_patient_volume_min ?? '',
        annual_patient_volume_max: pi.annual_patient_volume_max ?? '',
        locations: pi.locations ?? '',
        geographic_location: pi.geographic_location || '',
        years_in_practice: pi.years_in_practice ?? '',
        competing_studies: pi.competing_studies ?? '',
        board_certifications: (pi.board_certifications || []).join(', '),
        research_experience: (pi.research_experience || []).join('\n'),
      });
    } else {
      resetForm();
    }
    setDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
    resetForm();
  };

  const handleSubmit = async () => {
    if (!formData.name.trim()) {
      toast.error('Name is required');
      return;
    }

    // Convert UI strings into the backend payload shape
    const toIntOrNull = (v) => {
      if (v === '' || v === null || v === undefined) return null;
      const n = parseInt(v, 10);
      return Number.isFinite(n) ? n : null;
    };
    const payload = {
      name: formData.name,
      title: formData.title,
      specialty: formData.specialty,
      therapeutic_area: formData.therapeutic_area,
      site_location: formData.site_location,
      subspecialty: formData.subspecialty,
      practice_type: formData.practice_type,
      annual_patient_volume_min: toIntOrNull(formData.annual_patient_volume_min),
      annual_patient_volume_max: toIntOrNull(formData.annual_patient_volume_max),
      locations: toIntOrNull(formData.locations),
      geographic_location: formData.geographic_location,
      years_in_practice: toIntOrNull(formData.years_in_practice),
      competing_studies: toIntOrNull(formData.competing_studies),
      board_certifications: formData.board_certifications
        .split(',')
        .map(s => s.trim())
        .filter(Boolean),
      research_experience: formData.research_experience
        .split('\n')
        .map(s => s.trim())
        .filter(Boolean),
    };

    setSaving(true);
    try {
      if (editingPI) {
        await updatePI(editingPI.id, payload);
        toast.success('PI updated successfully');
      } else {
        await createPI(payload);
        toast.success('PI added successfully');
      }
      handleCloseDialog();
      await loadPIs();
    } catch (error) {
      toast.error(editingPI ? 'Failed to update PI' : 'Failed to add PI');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (piId) => {
    try {
      await deletePI(piId);
      toast.success('PI deleted');
      await loadPIs();
    } catch (error) {
      toast.error('Failed to delete PI');
    }
  };

  const handleExtractFromCV = async (pi) => {
    setBusyPi(prev => ({ ...prev, [pi.id]: 'extracting' }));
    try {
      const res = await extractPIFromCV(pi.id, true);
      const n = (res.extracted_fields || []).filter(f => f !== 'cv_extracted_at').length;
      toast.success(`Extracted ${n} fields from ${pi.name}'s CV`);
      await loadPIs();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Extraction failed');
    } finally {
      setBusyPi(prev => ({ ...prev, [pi.id]: null }));
    }
  };

  const handleRefreshDemographics = async (pi) => {
    setBusyPi(prev => ({ ...prev, [pi.id]: 'demo' }));
    try {
      const res = await refreshPIDemographics(pi.id, true);
      const len = res?.cache?.brief?.length || 0;
      toast.success(`Cached demographic brief (${len} chars) for ${res.location}`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Refresh failed');
    } finally {
      setBusyPi(prev => ({ ...prev, [pi.id]: null }));
    }
  };

  return (
    <div className="max-w-4xl mx-auto space-y-8 animate-fade-in">
      {/* Header */}
      <div>
        <h1 className="text-3xl font-bold text-slate-900 font-['Manrope']" data-testid="settings-title">
          Settings
        </h1>
        <p className="text-slate-600 mt-1">Manage principal investigators and site configuration</p>
      </div>

      {/* PI Management */}
      <Card className="stryde-card">
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="text-xl font-semibold font-['Manrope'] flex items-center gap-2">
                <User className="w-5 h-5 text-[#2D7DD2]" />
                Principal Investigators
              </CardTitle>
              <CardDescription>
                Manage the PIs available for feasibility assignments
              </CardDescription>
            </div>
            <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
              <DialogTrigger asChild>
                <Button 
                  className="btn-primary gap-2" 
                  onClick={() => handleOpenDialog()}
                  data-testid="add-pi-btn"
                >
                  <Plus className="w-4 h-4" />
                  Add PI
                </Button>
              </DialogTrigger>
              <DialogContent className="sm:max-w-2xl max-h-[85vh] overflow-y-auto">
                <DialogHeader>
                  <DialogTitle>{editingPI ? 'Edit PI' : 'Add Principal Investigator'}</DialogTitle>
                  <DialogDescription>
                    {editingPI ? 'Fields can be filled by AI from the CV — or edited here directly.' : 'Enter the details of the new PI'}
                  </DialogDescription>
                </DialogHeader>
                <div className="space-y-4 py-4">
                  <div className="grid grid-cols-2 gap-3">
                    <div className="space-y-2">
                      <Label htmlFor="name">Name *</Label>
                      <Input
                        id="name"
                        placeholder="Dr. John Smith"
                        value={formData.name}
                        onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                        data-testid="pi-name-input"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="title">Title</Label>
                      <Input
                        id="title"
                        placeholder="MD, PhD"
                        value={formData.title}
                        onChange={(e) => setFormData(prev => ({ ...prev, title: e.target.value }))}
                        data-testid="pi-title-input"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="specialty">Specialty (detailed)</Label>
                      <Input
                        id="specialty"
                        placeholder="Dermatology, Mohs Surgery"
                        value={formData.specialty}
                        onChange={(e) => setFormData(prev => ({ ...prev, specialty: e.target.value }))}
                        data-testid="pi-specialty-input"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="therapeutic_area">Therapeutic Area (canonical)</Label>
                      <Input
                        id="therapeutic_area"
                        placeholder="Dermatologist"
                        value={formData.therapeutic_area}
                        onChange={(e) => setFormData(prev => ({ ...prev, therapeutic_area: e.target.value }))}
                        data-testid="pi-ta-input"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="subspecialty">Subspecialty</Label>
                      <Input
                        id="subspecialty"
                        placeholder="Mohs Surgery, Dermatologic Oncology"
                        value={formData.subspecialty}
                        onChange={(e) => setFormData(prev => ({ ...prev, subspecialty: e.target.value }))}
                        data-testid="pi-subspecialty-input"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="practice_type">Practice type</Label>
                      <Input
                        id="practice_type"
                        placeholder="academic | community | hospital-based | multi-location community"
                        value={formData.practice_type}
                        onChange={(e) => setFormData(prev => ({ ...prev, practice_type: e.target.value }))}
                        data-testid="pi-practice-type-input"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="locations">Number of locations</Label>
                      <Input
                        id="locations"
                        type="number"
                        min="0"
                        placeholder="8"
                        value={formData.locations}
                        onChange={(e) => setFormData(prev => ({ ...prev, locations: e.target.value }))}
                        data-testid="pi-locations-input"
                      />
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="geographic_location">Geographic location</Label>
                    <Input
                      id="geographic_location"
                      placeholder="Clifton NJ + 7 NY/NJ sites"
                      value={formData.geographic_location}
                      onChange={(e) => setFormData(prev => ({ ...prev, geographic_location: e.target.value }))}
                      data-testid="pi-geo-input"
                    />
                  </div>

                  <div className="grid grid-cols-3 gap-3">
                    <div className="space-y-2">
                      <Label htmlFor="vol_min">Annual volume min</Label>
                      <Input
                        id="vol_min"
                        type="number"
                        min="0"
                        placeholder="12000"
                        value={formData.annual_patient_volume_min}
                        onChange={(e) => setFormData(prev => ({ ...prev, annual_patient_volume_min: e.target.value }))}
                        data-testid="pi-vol-min-input"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="vol_max">Annual volume max</Label>
                      <Input
                        id="vol_max"
                        type="number"
                        min="0"
                        placeholder="15000"
                        value={formData.annual_patient_volume_max}
                        onChange={(e) => setFormData(prev => ({ ...prev, annual_patient_volume_max: e.target.value }))}
                        data-testid="pi-vol-max-input"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="years">Years in practice</Label>
                      <Input
                        id="years"
                        type="number"
                        min="0"
                        placeholder="20"
                        value={formData.years_in_practice}
                        onChange={(e) => setFormData(prev => ({ ...prev, years_in_practice: e.target.value }))}
                        data-testid="pi-years-input"
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-3">
                    <div className="space-y-2">
                      <Label htmlFor="competing">Competing concurrent studies</Label>
                      <Input
                        id="competing"
                        type="number"
                        min="0"
                        placeholder="2"
                        value={formData.competing_studies}
                        onChange={(e) => setFormData(prev => ({ ...prev, competing_studies: e.target.value }))}
                        data-testid="pi-competing-input"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="location">Primary site location</Label>
                      <Input
                        id="location"
                        placeholder="Houston, TX"
                        value={formData.site_location}
                        onChange={(e) => setFormData(prev => ({ ...prev, site_location: e.target.value }))}
                        data-testid="pi-location-input"
                      />
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="board_certs">Board certifications (comma separated)</Label>
                    <Input
                      id="board_certs"
                      placeholder="American Board of Dermatology, American College of Mohs Surgery"
                      value={formData.board_certifications}
                      onChange={(e) => setFormData(prev => ({ ...prev, board_certifications: e.target.value }))}
                      data-testid="pi-boards-input"
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="research_exp">Research experience (one bullet per line)</Label>
                    <textarea
                      id="research_exp"
                      rows={5}
                      placeholder={"PI on 12 phase 2-3 dermatology trials since 2018\nMulti-center study for recurrent cutaneous SCC..."}
                      value={formData.research_experience}
                      onChange={(e) => setFormData(prev => ({ ...prev, research_experience: e.target.value }))}
                      className="w-full rounded-md border border-slate-200 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#2D7DD2]"
                      data-testid="pi-research-input"
                    />
                  </div>
                </div>
                <DialogFooter>
                  <Button variant="outline" onClick={handleCloseDialog}>
                    Cancel
                  </Button>
                  <Button 
                    onClick={handleSubmit} 
                    disabled={saving}
                    className="btn-primary"
                    data-testid="save-pi-btn"
                  >
                    {saving ? (
                      <>
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                        Saving...
                      </>
                    ) : (
                      <>
                        <Save className="w-4 h-4 mr-2" />
                        {editingPI ? 'Update' : 'Add'} PI
                      </>
                    )}
                  </Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>
          </div>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="space-y-3">
              {[1, 2, 3].map(i => (
                <Skeleton key={i} className="h-20" />
              ))}
            </div>
          ) : pis.length === 0 ? (
            <div className="text-center py-12">
              <div className="w-16 h-16 rounded-full bg-slate-100 flex items-center justify-center mx-auto mb-4">
                <User className="w-8 h-8 text-slate-400" />
              </div>
              <h3 className="text-lg font-semibold text-slate-900 mb-2">No PIs registered</h3>
              <p className="text-slate-500 mb-4">Add your first principal investigator to get started</p>
              <Button onClick={() => handleOpenDialog()} className="btn-primary">
                <Plus className="w-4 h-4 mr-2" />
                Add First PI
              </Button>
            </div>
          ) : (
            <div className="space-y-3">
              {pis.map((pi) => {
                const busy = busyPi[pi.id];
                const volume = (pi.annual_patient_volume_min || pi.annual_patient_volume_max)
                  ? `${pi.annual_patient_volume_min ?? '?'}–${pi.annual_patient_volume_max ?? '?'} pts/yr`
                  : null;
                return (
                  <div 
                    key={pi.id} 
                    className="p-4 border border-slate-200 rounded-lg hover:border-slate-300 transition-colors"
                    data-testid={`pi-card-${pi.id}`}
                  >
                    <div className="flex items-start gap-4">
                      <div className="w-12 h-12 rounded-full bg-purple-100 flex items-center justify-center shrink-0">
                        <User className="w-6 h-6 text-purple-600" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-start justify-between gap-3">
                          <h4 className="font-medium text-slate-900">{pi.name}</h4>
                          <div className="flex items-center gap-1 shrink-0">
                            {pi.cv_document_id && (
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => handleExtractFromCV(pi)}
                                disabled={!!busy}
                                title="Re-extract structured profile from the linked CV"
                                data-testid={`extract-cv-${pi.id}`}
                              >
                                {busy === 'extracting'
                                  ? <Loader2 className="w-4 h-4 mr-1 animate-spin" />
                                  : <Sparkles className="w-4 h-4 mr-1 text-amber-500" />}
                                Extract CV
                              </Button>
                            )}
                            {(pi.geographic_location || pi.site_location) && (
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => handleRefreshDemographics(pi)}
                                disabled={!!busy}
                                title="Refresh demographics cache for this PI's geography"
                                data-testid={`refresh-demo-${pi.id}`}
                              >
                                {busy === 'demo'
                                  ? <Loader2 className="w-4 h-4 mr-1 animate-spin" />
                                  : <Globe2 className="w-4 h-4 mr-1 text-emerald-600" />}
                                Demographics
                              </Button>
                            )}
                            <Button
                              variant="ghost"
                              size="icon"
                              onClick={() => handleOpenDialog(pi)}
                              data-testid={`edit-pi-${pi.id}`}
                            >
                              <Edit2 className="w-4 h-4 text-slate-500" />
                            </Button>
                            <AlertDialog>
                              <AlertDialogTrigger asChild>
                                <Button variant="ghost" size="icon" className="text-red-500 hover:text-red-600 hover:bg-red-50">
                                  <Trash2 className="w-4 h-4" />
                                </Button>
                              </AlertDialogTrigger>
                              <AlertDialogContent>
                                <AlertDialogHeader>
                                  <AlertDialogTitle>Delete PI</AlertDialogTitle>
                                  <AlertDialogDescription>
                                    Are you sure you want to delete {pi.name}? This action cannot be undone.
                                  </AlertDialogDescription>
                                </AlertDialogHeader>
                                <AlertDialogFooter>
                                  <AlertDialogCancel>Cancel</AlertDialogCancel>
                                  <AlertDialogAction
                                    className="bg-red-600 hover:bg-red-700"
                                    onClick={() => handleDelete(pi.id)}
                                    data-testid={`confirm-delete-pi-${pi.id}`}
                                  >
                                    Delete
                                  </AlertDialogAction>
                                </AlertDialogFooter>
                              </AlertDialogContent>
                            </AlertDialog>
                          </div>
                        </div>
                        <div className="flex flex-wrap items-center gap-x-4 gap-y-1 mt-1 text-sm text-slate-500">
                          {pi.title && (<span className="flex items-center gap-1"><Building2 className="w-3.5 h-3.5" />{pi.title}</span>)}
                          {(pi.subspecialty || pi.specialty) && (<span className="flex items-center gap-1"><Stethoscope className="w-3.5 h-3.5" />{pi.subspecialty || pi.specialty}</span>)}
                          {(pi.geographic_location || pi.site_location) && (<span className="flex items-center gap-1"><MapPin className="w-3.5 h-3.5" />{pi.geographic_location || pi.site_location}</span>)}
                        </div>
                        <div className="flex flex-wrap gap-2 mt-2">
                          {pi.therapeutic_area && (<span className="px-2 py-0.5 rounded-full bg-indigo-50 text-xs font-semibold text-indigo-700">{pi.therapeutic_area}</span>)}
                          {pi.practice_type && (<span className="px-2 py-0.5 rounded-full bg-slate-100 text-xs text-slate-700">{pi.practice_type}</span>)}
                          {pi.locations ? (<span className="px-2 py-0.5 rounded-full bg-slate-100 text-xs text-slate-700 flex items-center gap-1"><Building2 className="w-3 h-3" />{pi.locations} location{pi.locations === 1 ? '' : 's'}</span>) : null}
                          {volume && (<span className="px-2 py-0.5 rounded-full bg-emerald-50 text-xs text-emerald-700 flex items-center gap-1"><Users className="w-3 h-3" />{volume}</span>)}
                          {pi.years_in_practice ? (<span className="px-2 py-0.5 rounded-full bg-slate-100 text-xs text-slate-700">{pi.years_in_practice}y experience</span>) : null}
                          {pi.competing_studies !== null && pi.competing_studies !== undefined ? (<span className="px-2 py-0.5 rounded-full bg-amber-50 text-xs text-amber-700 flex items-center gap-1"><Activity className="w-3 h-3" />{pi.competing_studies} competing</span>) : null}
                          {pi.cv_extracted_at && (<span className="px-2 py-0.5 rounded-full bg-blue-50 text-xs text-blue-700">CV parsed</span>)}
                        </div>
                        {pi.board_certifications && pi.board_certifications.length > 0 && (
                          <p className="text-xs text-slate-500 mt-2"><strong>Boards:</strong> {pi.board_certifications.join(' · ')}</p>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Additional Settings Card */}
      <Card className="stryde-card">
        <CardHeader>
          <CardTitle className="text-xl font-semibold font-['Manrope']">
            Application Info
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            <div className="flex items-center justify-between p-4 bg-slate-50 rounded-lg">
              <div>
                <p className="font-medium text-slate-900">StrydeFeast Version</p>
                <p className="text-sm text-slate-500">Clinical Feasibility Copilot</p>
              </div>
              <span className="text-sm font-mono text-slate-600">v1.0.0</span>
            </div>
            <div className="flex items-center justify-between p-4 bg-slate-50 rounded-lg">
              <div>
                <p className="font-medium text-slate-900">AI Model</p>
                <p className="text-sm text-slate-500">Response generation powered by</p>
              </div>
              <span className="text-sm font-mono text-slate-600">OpenAI GPT-5.2</span>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
